import clsx from 'clsx';
import { forwardRef, type PropsWithChildren, useRef } from 'react';

import { MultiMeterBarChartLegend } from './components/MultiMeterBarChartLegend.js';
import { MultiMeterBarChartPlotArea } from './components/MultiMeterBarChartPlotArea.js';
import { MULTIMETER_BAR_CHART_ARIA_LABEL } from './constants.js';
import { useMultiMeterBarChartConfig } from './hooks/useMultiMeterBarChartConfig.js';
import { MultiMeterBarChartProviders } from './providers/MultiMeterBarChartProviders.js';
import { Label } from './slots/Label.js';
import { Legend } from './slots/Legend.js';
import { Max } from './slots/Max.js';
import { Min } from './slots/Min.js';
import { Segment } from './slots/Segment.js';
import { Tooltip } from './slots/Tooltip.js';
import { Value } from './slots/Value.js';
import type { MultiMeterBarChartConsumerConfig } from './types/multi-meter-bar-chart-config.js';
import type { MultiMeterBarChartProps } from './types/multi-meter-bar-chart.js';
import { useBehavioralTrackingProps } from '../../../core/hooks/useBehavioralTrackingProps.js';
import { Intent } from '../../../core/slots/Intent/Intent.js';
import { defaultChartSize } from '../../../core/styles/defaultChartValues.sty.js';
import { ColorRule } from '../../core/slots/ColorRule.js';
import { ChartType } from '../../core/types/chart-types.js';
import { MeterBarChartFormattedValue } from '../shared/components/formatted-value/MeterBarChartFormattedValue.js';
import { MeterBarLayout } from '../shared/components/meter-bar-layout/MeterBarLayout.js';
import { MIN_DEFAULT_VALUE, SIZE_DEFAULT } from '../shared/constants.js';
import { Toolbar } from '../shared/slots/Toolbar.js';
import type { MultiMeterBarChartElementRef } from '../shared/types/meter-bars.js';

const COMPONENT_NAME = 'MultiMeterBarChart';
/**
 * Multi meter bar chart component
 */
const MultiMeterBarChartRoot = forwardRef<
  MultiMeterBarChartElementRef,
  PropsWithChildren<MultiMeterBarChartProps>
>((props, forwardedRef) => {
  const {
    width,
    height,
    className,
    style,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
    'data-testid': dataTestId = 'multi-meter-bar-chart-root',
    seriesActions,
    prefixIcon,
  } = props;

  const [behavioralTrackingProps] = useBehavioralTrackingProps(props);

  const config = useMultiMeterBarChartConfig(props);

  const {
    colorPalette,
    size = SIZE_DEFAULT,
    min = MIN_DEFAULT_VALUE,
    max,
    loading,
    formatter,
    unit,
    labelSlot,
    valueSlot,
    minSlot,
    maxSlot,
    segments,
    legend,
    tooltip,
    totalValue,
    colorRules,
    toolbar,
  } = config;

  const multiMeterBarChartRef = useRef<HTMLDivElement>(null);

  const consumerConfig: MultiMeterBarChartConsumerConfig & {
    type: ChartType.MultiMeterBar;
  } = {
    type: ChartType.MultiMeterBar,
    colorPalette,
    size,
    min,
    max,
    formatter,
    unit,
    tooltip,
    legend,
    loading,
  };

  const label = labelSlot ?? '';

  return (
    <MultiMeterBarChartProviders
      colorPalette={colorPalette}
      formatter={formatter}
      unit={unit}
      segments={segments}
      colorRules={colorRules}
      toolbar={toolbar}
      loading={loading}
      seriesActions={seriesActions}
    >
      <MeterBarLayout
        {...behavioralTrackingProps}
        width={width}
        height={height}
        ref={multiMeterBarChartRef}
        size={size}
        className={clsx(defaultChartSize, className)}
        style={style}
        data-dtrum-mask={dataDtrumMask}
        data-dtrum-allow={dataDtrumAllow}
        data-testid={dataTestId}
        aria-label={MULTIMETER_BAR_CHART_ARIA_LABEL}
        data-dt-component={COMPONENT_NAME}
        consumerConfig={consumerConfig}
        userRef={forwardedRef}
      >
        {prefixIcon && <MeterBarLayout.Icon>{prefixIcon}</MeterBarLayout.Icon>}
        {labelSlot && <MeterBarLayout.Label>{labelSlot}</MeterBarLayout.Label>}

        {valueSlot && (
          <MeterBarLayout.Value>
            <MeterBarChartFormattedValue
              value={totalValue}
              total={max - min}
              auto={valueSlot?.auto}
            >
              {valueSlot?.value}
            </MeterBarChartFormattedValue>
          </MeterBarLayout.Value>
        )}

        <MeterBarLayout.Chart>
          <MultiMeterBarChartPlotArea
            label={label}
            max={max}
            min={min}
            tooltipConfig={tooltip}
          />
        </MeterBarLayout.Chart>

        {minSlot && (
          <MeterBarLayout.Min>
            <MeterBarChartFormattedValue value={min} auto={minSlot?.auto}>
              {minSlot?.value}
            </MeterBarChartFormattedValue>
          </MeterBarLayout.Min>
        )}

        {maxSlot && (
          <MeterBarLayout.Max>
            <MeterBarChartFormattedValue value={max} auto={maxSlot?.auto}>
              {maxSlot?.value}
            </MeterBarChartFormattedValue>
          </MeterBarLayout.Max>
        )}

        {legend && (
          <MeterBarLayout.Legend>
            <MultiMeterBarChartLegend />
          </MeterBarLayout.Legend>
        )}
      </MeterBarLayout>
    </MultiMeterBarChartProviders>
  );
});

(
  MultiMeterBarChartRoot as typeof MultiMeterBarChartRoot & {
    displayName: string;
  }
).displayName = '_MultiMeterBarChart';

/**
 * The `MultiMeterBarChart` is a more complex version of the single meter bar,
 * it allows for different data inputs to be considered when presenting progress
 * toward a goal, consumption of a total, etc.
 * @public
 */
export const MultiMeterBarChart = Object.assign(MultiMeterBarChartRoot, {
  Label,
  Legend,
  Max,
  Min,
  Segment,
  Value,
  Tooltip,
  ColorRule,
  Intent,
  Toolbar,
});
